import React from 'react';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface SuccessStory {
  id: string;
  company: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
    icon: string;
  }[];
  quote: string;
  author: string;
  role: string;
  image: string;
  alt: string;
}

interface SuccessStoriesProps {
  className?: string;
}

const SuccessStories: React.FC<SuccessStoriesProps> = ({ className = '' }) => {
  const stories: SuccessStory[] = [
  {
    id: 'telecom-leader',
    company: 'European Telecom Group',
    industry: 'Telecommunications',
    challenge: 'High customer churn due to poor support experience and network issues',
    solution: 'Implemented KnowMax + DeviceMax for intelligent support and proactive device management',
    results: [
    { metric: 'Improvement in documentation', value: '21%', icon: 'FaceSmileIcon' },
    { metric: 'Improvement in FCR', value: '12%', icon: 'ArrowTrendingDownIcon' },
    { metric: 'Saved annually', value: '$400,000', icon: 'CurrencyDollarIcon' }],

    quote: 'UnifyMax transformed our customer support from reactive to proactive. The AI-powered insights helped us prevent issues before customers even noticed them.',
    author: 'Sarah Chen',
    role: 'Chief Customer Officer',
    image: "/assets/images/15_-1771919860863.jpg",
    alt: 'Professional Asian woman executive in business attire smiling confidently in modern office environment'
  },
  {
    id: 'fortune-bank',
    company: 'Large ISP/Broadband provider ',
    industry: 'Broadband Service Provider',
    challenge: 'Fragmented customer experience across digital and physical channels',
    solution: 'Deployed PhygitalMax + LeapMax for unified omnichannel experience and process automation',
    results: [
    { metric: 'Improvement in SOP adherence', value: '20%', icon: 'DevicePhoneMobileIcon' },
    { metric: 'AHT reduction', value: '33%', icon: 'BoltIcon' },
    { metric: 'Saved annually on reduced AHT', value: '$185,000', icon: 'BanknotesIcon' }],

    quote: 'The seamless integration between our branches and digital platforms has revolutionized how we serve customers. Our NPS scores have never been higher.',
    author: 'Michael Rodriguez',
    role: 'Head of Digital Banking',
    image: "/assets/images/115_-1771920404136.jpg",
    alt: 'Professional male executive in business suit with confident smile in modern corporate office with city skyline'
  }];


  return (
    <section className={`py-20 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-success/10 rounded-full px-4 py-2 mb-6">
            <Icon name="TrophyIcon" size={20} variant="solid" className="text-success" />
            <span className="font-body text-sm font-medium text-success">Customer Success</span>
          </div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Proven Transformation Outcomes
          </h2>
          <p className="font-body text-base text-muted-foreground">
            Real results from enterprise leaders who transformed with UnifyMax
          </p>
        </div>

        {/* Success Stories */}
        <div className="space-y-12">
          {stories.map((story, index) =>
          <div
            key={story.id}
            className={`bg-card rounded-2xl shadow-xl overflow-hidden border border-border ${
            index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex flex-col lg:flex`
            }>

              {/* Image Section */}
              <div className="lg:w-2/5 relative h-50 lg:h-auto">
                <AppImage
                src={story.image}
                alt={story.alt}
                className="w-full h-full object-cover" />

                <div className="inset-0 bg-gradient-to-t from-primary/80 to-transparent lg:bg-gradient-to-r"></div>
                <div className="absolute bottom-6 left-6 right-6 lg:bottom-8 lg:left-8">
                  <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-3">
                    <Icon name="BuildingOfficeIcon" size={16} variant="solid" className="text-white" />
                    <span className="font-body text-sm font-medium text-white">{story.industry}</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl text-white">{story.company}</h3>
                </div>
              </div>

              {/* Content Section */}
              <div className="lg:w-3/5 px-8 lg:px-12 pt-8 lg:pt-8 pb-4 lg:pb-4 space-y-4">
                {/* Challenge & Solution */}
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="ExclamationTriangleIcon" size={20} variant="solid" className="text-warning" />
                      <span className="font-display font-semibold text-xl text-warning uppercase tracking-wide">
                        Challenge
                      </span>
                    </div>
                    <p className="font-body text-lg text-muted-foreground leading-relaxed">
                      {story.challenge}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="LightBulbIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-display font-semibold text-xl text-accent uppercase tracking-wide">
                        Solution
                      </span>
                    </div>
                    <p className="font-body text-lg text-muted-foreground leading-relaxed">
                      {story.solution}
                    </p>
                  </div>
                </div>

                {/* Results Grid */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border pb-0">
                  {story.results.map((result, idx) =>
                <div key={idx} className="text-center space-y-1 flex flex-col items-center justify-start p-4 bg-[rgba(39,106,137,0.1412)] rounded-br-[20px] rounded-t-[20px] rounded-bl-[20px]">
                      <Icon
                    name={result.icon as any}
                    size={36}
                    variant="solid"
                    className="text-primary mx-auto" />

                      <div className="font-display font-bold text-3xl leading-tight">{result.value}</div>
                      <div className="font-body text-lg text-muted-foreground leading-snug">{result.metric}</div>
                    </div>
                )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default SuccessStories;